import "./ControlloKmOre.css";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select, { components } from "react-select";
import Lf23Loader from "../../../components/Lf23Loader";

// Componente custom per mostrare max 2 opzioni + badge con contatore
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);

    // Mostra solo i primi 2 elementi
    if (index < 2) {
        return (
            <components.MultiValue selectProps={selectProps} data={data} {...props}>
                {children}
            </components.MultiValue>
        );
    }
    // Al terzo elemento, mostra il badge "+N"
    if (index === 2 && values.length > 2) {
        return (
            <div className="controllo-km-ore-select-more-badge">
                +{values.length - 2}
            </div>
        );
    }
    // Gli altri elementi non vengono renderizzati
    return null;
};

const ControlloKmOre = ({ filters, loading: parentLoading = false, filterOptions = {} }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // opzioni per i dropdown
    const colonneOptions = [
        { value: 'anno', label: 'Anno' },
        { value: 'trimestre', label: 'Trimestre' },
        { value: 'mese', label: 'Mese' },
    ];

    const righeOptions = [
        { value: 'cantiere', label: 'Cantiere' },
        { value: 'sottotipologia', label: 'Sottotipologia' },
        { value: 'tipologia', label: 'Tipologia' },
        { value: 'targa', label: 'Targa' },
        { value: 'settore', label: 'Settore' },
        { value: 'commessa', label: 'Commessa' },
        { value: 'attivita_livello_1', label: 'Attività Livello 1' },
        { value: 'attivita_livello_2', label: 'Attività Livello 2' },
        { value: 'attivita_livello_3', label: 'Attività Livello 3' },
        { value: 'attivita_livello_4', label: 'Attività Livello 4' },
    ];

    const indicatoreOptions = [
        { value: 'km-percorsi', label: 'Km Percorsi' },
        { value: 'ore-guidate', label: 'Ore Guidate' },
        { value: 'vetusta', label: 'Vetustà' },
        { value: 'km-ora', label: 'Km all\'ora' },
        { value: 'vel-media', label: 'Vel Media' },
        { value: 'costo-km', label: 'Costo al Km' },
        { value: 'costo-totale-automezzi', label: 'Costo Totale Automezzi' },
    ];

    // stati per i dropdown
    const [selectedColonne, setSelectedColonne] = useState([colonneOptions.find(opt => opt.value === 'mese')]);
    const [selectedRighe, setSelectedRighe] = useState([
        righeOptions.find(opt => opt.value === 'sottotipologia'),
        righeOptions.find(opt => opt.value === 'targa'),
    ]);
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);

    // stato per i dati del backend
    const [datiBackend, setDatiBackend] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);

    const [isFullScreenTable, setIsFullScreenTable] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullScreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullScreenTable]);

    // chiamata api per ottenere i dati
    useEffect(() => {
        const fetchDati = async () => {
            setDataLoading(true);
            try {
                // Costruisco i parametri dalla prop filters
                const params = new URLSearchParams();

                // Date (obbligatorie per le performance)
                if (filters?.fromDate) params.append('dataInizio', filters.fromDate);
                if (filters?.toDate) params.append('dataFine', filters.toDate);

                // Altri filtri (array di oggetti con value)
                if (filters?.settore?.length > 0) {
                    params.append('settore', filters.settore.map(s => s.value).join(','));
                }
                if (filters?.cantiere?.length > 0) {
                    params.append('cantiere', filters.cantiere.map(c => c.value).join(','));
                }
                if (filters?.targa?.length > 0) {
                    params.append('targa', filters.targa.map(t => t.value).join(','));
                }
                if (filters?.tipologiaVeicolo?.length > 0) {
                    params.append('tipologia', filters.tipologiaVeicolo.map(t => t.value).join(','));
                }

                const url = `${API_URL}/operation-automezzi/controllo-km-ore?${params.toString()}`;
                const response = await axios.get(url);

                // Lookup map: traduci ID in nomi usando filterOptions
                const settoreMap = {};
                (filterOptions.settori || []).forEach(s => { settoreMap[s.value] = s.label; });
                const commessaMap = {};
                (filterOptions.commesse || []).forEach(c => { commessaMap[c.value] = c.label; });
                const attivitaMap = {};
                (filterOptions.attivita || []).forEach(a => { attivitaMap[a.id] = a; });

                const datiConNomi = response.data.map(riga => {
                    const att = attivitaMap[riga.commessa_attivita_id] || {};
                    return {
                        ...riga,
                        settore: settoreMap[riga.id_settore] || 'Non assegnato',
                        commessa: commessaMap[riga.id_commessa] || 'Non assegnato',
                        attivita_livello_1: att.livello_1 || 'Non assegnato',
                        attivita_livello_2: att.livello_2 || 'Non assegnato',
                        attivita_livello_3: att.livello_3 || 'Non assegnato',
                        attivita_livello_4: att.livello_4 || 'Non assegnato',
                    };
                });

                setDatiBackend(datiConNomi);
            } catch (error) {
                console.error('Errore nel caricamento dati:', error);
            } finally {
                setDataLoading(false);
            }
        };

        fetchDati();
    }, [filters, API_URL, filterOptions]);

    // Validazione formato dati
    const hasValidDataFormat = datiBackend !== null && Array.isArray(datiBackend);

    // Combina loading del padre con validazioni locali
    const isLoading = parentLoading || dataLoading || !hasValidDataFormat;
    const isDataReady = !parentLoading && !dataLoading && hasValidDataFormat && datiBackend.length > 0;
    const isEmpty = !parentLoading && !dataLoading && hasValidDataFormat && datiBackend.length === 0;


    // FUNZIONE PER SCARICARE IL FILE EXCEL
    // Questa funzione:
    // 1. Riceve i dati da esportare
    // 2. Fa una chiamata POST al backend
    // 3. Crea un link invisibile e lo clicca per far partire il download
    const handleDownloadFile = async (datiDaEsportare) => {
        try {
            // faccio la chiamata post al backend
            // primo parametro: url della chiamata
            // secondo parametro: oggetto con i dati da inviare
            // terzo paramentro: opzioni (responseType: "blob" perchè ci aspettiamo un file)
            const response = await axios.post(`${API_URL}/operation-automezzi/downloadExcel`,
                {
                    tableName: 'Controllo Km-Ore',   // nome della tabella che apparirà nel file
                    datiDaStampare: datiDaEsportare
                },
                { responseType: "blob" } //blob significa file binario
            );

            // creo un url temporaneo per il file scaricato
            // window.URL.createObjectURL() prende i dati binari e crea un link
            const url = window.URL.createObjectURL(new Blob([response.data]));

            // creo un elemento <a> invisibile
            const link = document.createElement("a");

            // imposto l'url del link
            link.href = url;

            // imposto il nome del file che verrà scaricato
            link.setAttribute("download", "controllo_km_ore.xlsx");

            // aggiungo il link al documento
            document.body.appendChild(link);

            // clicco il link programmaticamente per far partire il download
            link.click();

            // rimuoivo il link dal documento per fare pulizia
            link.parentNode.removeChild(link);

            // libero la memoria dell'url temporaneo
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file: ", error);
        }
    };

    // FUNZIONE PER PREPARARE I DATI DA ESPORTARE
    // Questa funzione trasforma i dati della tabella in un formato semplice:
    // - headers: array con i nomi delle colonne ["Settore", "Km Percorsi", ...]
    // - rows: array di array, dove ogni array interno è una riga della tabella
    const prepareExportData = () => {
        // se non ci sono dati non posso esportare niente
        if (!datiTrasformati || datiTrasformati.length === 0) {
            return null;
        }

        // 1 - creo le intestazioni delle colonne

        const headers = ['Settore'];

        // per ogni periodo, aggiungo le colonne degli indicatori visibili
        // esempio: se ho Gennaio e Febbraio, e mostro Km e Ore,
        // avrò: ["Settore", "Gen - Km", "Gen - Ore", "Feb - Km", "Feb - Ore"]
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            const periodo = periodiDaMostrare[i];

            // per ogni indicatore visibile, aggiugno una colonna
            if (isColonnaVisibile('km-percorsi')) {
                headers.push(`${periodo.etichetta} - Km Percorsi`);
            }
            if (isColonnaVisibile('ore-guidate')) {
                headers.push(`${periodo.etichetta} - Ore Guidate`);
            }
            if (isColonnaVisibile('vetusta')) {
                headers.push(`${periodo.etichetta} - Vetustà`);
            }
            if (isColonnaVisibile('km-ora')) {
                headers.push(`${periodo.etichetta} - Km/Ora`);
            }
            if (isColonnaVisibile('vel-media')) {
                headers.push(`${periodo.etichetta} - Vel Media`);
            }
            if (isColonnaVisibile('costo-km')) {
                headers.push(`${periodo.etichetta} - Costo Km`);
            }
            if (isColonnaVisibile('costo-totale-automezzi')) {
                headers.push(`${periodo.etichetta} - Costo Totale`);
            }
        }

        // 2 - creo le righe con i dati
        const rows = [];

        // funzione helper che aggiunge una riga all'array rows
        // - label: il nome della riga (es. "Settore A" o "  Commessa 1")
        // - periodi: l'oggetto con i dati per ogni periodo
        // - vetusta: età media (in anni) della riga, costante tra i periodi
        const aggiungiRiga = (label, periodi, vetusta = 0) => {
            const riga = [label]; //prima cella: nome della riga

            // per ogni periodo, aggiungo i valori degli indicatori visibili
            for (let i = 0; i < periodiDaMostrare.length; i++) {
                const periodo = periodiDaMostrare[i];
                const datiPeriodo = periodi[periodo.chiave] || {
                    kmPercorsi: 0, oreGuidate: 0, kmOra: 0, velMedia: 0, costoKm: 0, costoTotaleAutomezzi: 0
                };

                if (isColonnaVisibile('km-percorsi')) {
                    riga.push(datiPeriodo.kmPercorsi);
                }
                if (isColonnaVisibile('ore-guidate')) {
                    riga.push(datiPeriodo.oreGuidate);
                }
                if (isColonnaVisibile('vetusta')) {
                    riga.push(vetusta);
                }
                if (isColonnaVisibile('km-ora')) {
                    riga.push(datiPeriodo.kmOra);
                }
                if (isColonnaVisibile('vel-media')) {
                    riga.push(datiPeriodo.velMedia);
                }
                if (isColonnaVisibile('costo-km')) {
                    riga.push(datiPeriodo.costoKm);
                }
                if (isColonnaVisibile('costo-totale-automezzi')) {
                    riga.push(datiPeriodo.costoTotaleAutomezzi);
                }
            }

            rows.push(riga);
        }

        // 3 - ciclo su tutti i dati e aggiugno le righe
        // esporto tutti i livelli
        for (let i = 0; i < datiTrasformati.length; i++) {
            const padre = datiTrasformati[i];
            aggiungiRiga(padre.label, padre.periodi, padre.vetusta || 0);

            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                aggiungiRiga('  ' + figlio.label, figlio.periodi, figlio.vetusta || 0);

                for (let l = 0; l < figlio.children.length; l++) {
                    const nipote = figlio.children[l];
                    aggiungiRiga('   ' + nipote.label, nipote.periodi, nipote.vetusta || 0);

                    for (let k = 0; k < nipote.children.length; k++) {
                        const pronipote = nipote.children[k];
                        aggiungiRiga('    ' + pronipote.label, pronipote.periodi, pronipote.vetusta || 0);
                    }
                }
            }
        }

        // 4 - aggiungo la riga totale
        aggiungiRiga('TOTALE', totaliPerPeriodo, vetustaTotale);

        // ritorno l'oggetto con headers e rows
        return { headers, rows };
    };

    // LISTA DEGLI ANNI DISPONIBILI 
    const anniDisponibili = useMemo(() => {
        if (!datiBackend || datiBackend.length === 0) return [];
        const anni = [...new Set(datiBackend.map(d => String(d.anno)))].sort();
        return anni;
    }, [datiBackend]);

    // MESI DISPONIBILI PER OGNI ANNO
    const mesiDisponibili = useMemo(() => {
        if (!datiBackend || datiBackend.length === 0) return {};
        const mesiPerAnno = {};
        datiBackend.forEach(d => {
            const anno = String(d.anno);
            const mese = Number(d.mese);
            if (!mesiPerAnno[anno]) {
                mesiPerAnno[anno] = new Set();
            }
            mesiPerAnno[anno].add(mese);
        });
        // Converto i Set in array ordinati
        Object.keys(mesiPerAnno).forEach(anno => {
            mesiPerAnno[anno] = [...mesiPerAnno[anno]].sort((a, b) => a - b);
        });
        return mesiPerAnno;
    }, [datiBackend]);

    // indice 0 -> gennaio / indice 11 -> dicembre
    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',];

    // Mappa mese -> trimestre
    const meseToTrimestre = {
        1: 1, 2: 1, 3: 1,
        4: 2, 5: 2, 6: 2,
        7: 3, 8: 3, 9: 3,
        10: 4, 11: 4, 12: 4
    };

    // Funzione per verificare se un trimestre ha dati
    const isTrimestreValido = (anno, trimestre) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.some(m => meseToTrimestre[m] === trimestre);
    };

    // Funzione per verificare se un mese ha dati
    const isMeseValido = (anno, mese) => {
        const mesiAnno = mesiDisponibili[anno] || [];
        return mesiAnno.includes(mese);
    };

    // FUNZIONE PER OTTENERE I PERIODI DA MOSTRARE
    // Questa funzione guarda cosa ha selezionato l'utente
    // nel dropdown "Dimensioni Colonne" e restituisce
    // una lista di periodi da visualizzare nella tabella
    const getPeriodiDaMostrare = () => {
        // Se l'utente non ha selezionato nulla, mostro solo i totali annuali + totale generale
        if (selectedColonne.length === 0) {
            return [
                { chiave: '2025', etichetta: '2025', anno: '2025' },
                { chiave: '2026', etichetta: '2026', anno: '2026' },
                { chiave: 'totale', etichetta: 'Totale', anno: 'totale' }
            ];
        }

        // creo un array vuoto dove metterò i periodi
        let periodi = [];

        // controllo quali colonne sono selezionate
        const haTrimestreSelezionato = selectedColonne.some(opt => opt.value === 'trimestre');
        // .some() scorre l'array e restituisce TRUE se almeno un elemento soddisfa la condizione
        const haMeseSelezionato = selectedColonne.some(opt => opt.value === 'mese');
        const haAnnoSelezionato = selectedColonne.some(opt => opt.value === 'anno');

        // per ogni anno, aggiungo i suoi mrsi e trimestri
        for (let i = 0; i < anniDisponibili.length; i++) {
            const anno = anniDisponibili[i];

            if (haMeseSelezionato) {
                // ciclo da 1 a 12 mesi
                for (let m = 1; m <= 12; m++) {
                    if (isMeseValido(anno, m)) {
                        // converto il numero del mese in stringa con 2 cifre
                        const meseNumero = String(m).padStart(2, '0');

                        periodi.push({
                            chiave: `${anno}-${meseNumero}`, // '2025-01'
                            etichetta: nomiMesi[m - 1],
                            anno: anno
                        })
                    }
                }
            }

            // aggiungo i trimestri di questo anno (se selezionati)
            // Mostro solo i trimestri esistenti (non futuri)
            else if (haTrimestreSelezionato) {
                if (isTrimestreValido(anno, 1)) periodi.push({ chiave: `${anno}-trim1`, etichetta: 'T1', anno: anno });
                if (isTrimestreValido(anno, 2)) periodi.push({ chiave: `${anno}-trim2`, etichetta: 'T2', anno: anno });
                if (isTrimestreValido(anno, 3)) periodi.push({ chiave: `${anno}-trim3`, etichetta: 'T3', anno: anno });
                if (isTrimestreValido(anno, 4)) periodi.push({ chiave: `${anno}-trim4`, etichetta: 'T4', anno: anno });
            }

            else if (haAnnoSelezionato) {
                periodi.push({
                    chiave: anno,
                    etichetta: anno,
                    anno: anno
                })
            }
        }

        // aggiungo il TOTALE alla fine
        periodi.push({ chiave: 'totale', etichetta: 'Totale', anno: 'totale' });

        return periodi;
    };

    // stato per le righe espanse
    const [expandedRows, setExpandedRows] = useState([]);

    // toggle per espandere o chiudere le righe
    const toggleRowExpansion = (rowKey) => {
        if (expandedRows.includes(rowKey)) {
            // se è aperta, la chiudo
            setExpandedRows(expandedRows.filter(id => id !== rowKey));
        } else {
            // è chiusa -> la apro
            setExpandedRows([...expandedRows, rowKey]);
        }
    };

    // controllo se una riga è aperta o chiusa
    const isRowExpanded = (rowKey) => expandedRows.includes(rowKey);

    // formattazioni numeri in stile italiano
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1
        }).format(num);
    };

    // Età (in anni, decimale) alla data odierna a partire da data di immatricolazione
    const calcEtaAnni = (dataImm) => {
        if (!dataImm) return null;
        const d = new Date(dataImm);
        if (isNaN(d.getTime())) return null;
        const eta = (Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
        return eta >= 0 ? eta : null;
    };

    // Vetustà media a partire da una mappa targa→data_immatricolazione
    const calcolaVetustaMedia = (targheMap) => {
        if (!targheMap) return 0;
        let sum = 0;
        let count = 0;
        for (const targa in targheMap) {
            const e = calcEtaAnni(targheMap[targa]);
            if (e != null) { sum += e; count++; }
        }
        return count > 0 ? Math.ceil(sum / count) : 0;
    };

    // FUNZIONE PER TRASFORMARE I DATI DEL BACKEND
    // COSA FA QUESTA FUNZIONE:
    // 1. Prende i dati "piatti" dal backend
    // 2. Li raggruppa in base a selectedRighe
    // 3. Crea i periodi aggregati (trimestri, anni, totale)
    // 4. Calcola kmOra e velMedia per ogni periodo
    //
    const trasformaDati = (datiGrezzi) => {

        // PASSO 1: Determino i campi di raggruppamento fino a 4 livelli
        let campoLivello1 = 'settore';
        let campoLivello2 = null;
        let campoLivello3 = null;
        let campoLivello4 = null;

        if (selectedRighe.length > 0) {
            campoLivello1 = selectedRighe[0].value;
        }
        if (selectedRighe.length > 1) {
            campoLivello2 = selectedRighe[1].value;
        }
        if (selectedRighe.length > 2) {
            campoLivello3 = selectedRighe[2].value;
        }
        if (selectedRighe.length > 3) {
            campoLivello4 = selectedRighe[3].value;
        }

        const raggruppati = {};

        const creaPeriodoVuoto = () => ({
            kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
            velMediaPesata: 0,  // somma di (velMedia * kmPercorsi)
            kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
        });

        // PASSO 2: Raggruppo i dati per tutti i livelli selezionati
        for (let i = 0; i < datiGrezzi.length; i++) {
            const riga = datiGrezzi[i];

            // Estraggo i valori dei campi per ogni livello
            const valore1 = riga[campoLivello1] || 'Non assegnato';
            const valore2 = campoLivello2 ? (riga[campoLivello2] || 'Non assegnato') : null;
            const valore3 = campoLivello3 ? (riga[campoLivello3] || 'Non assegnato') : null;
            const valore4 = campoLivello4 ? (riga[campoLivello4] || 'Non assegnato') : null;

            // Estraggo i valori numerici
            const kmPercorsi = Number(riga.km_percorsi) || 0;
            const oreGuidate = Number(riga.ore_guidate) || 0;
            const velMedia = Number(riga.vel_media) || 0;

            // Creo la chiave del periodo: "2025-01" ecc
            const meseNumero = String(riga.mese).padStart(2, '0');
            const chiavePeriodo = `${riga.anno}-${meseNumero}`;

            // Creo il LIVELLO 1 (padre) se non esiste
            if (!raggruppati[valore1]) {
                raggruppati[valore1] = {
                    id: valore1,
                    label: valore1,
                    periodi: {},
                    children: [],
                    childrenMap: {},
                    targheMap: {}
                };
            }
            raggruppati[valore1].targheMap[riga.targa] = riga.data_immatricolazione;

            // Se non c'è livello 2, accumulo direttamente nel livello 1
            if (!campoLivello2) {
                if (!raggruppati[valore1].periodi[chiavePeriodo]) {
                    raggruppati[valore1].periodi[chiavePeriodo] = creaPeriodoVuoto();
                }
                raggruppati[valore1].periodi[chiavePeriodo].kmPercorsi += kmPercorsi;
                raggruppati[valore1].periodi[chiavePeriodo].oreGuidate += oreGuidate;
                raggruppati[valore1].periodi[chiavePeriodo].velMediaPesata += velMedia * oreGuidate;

            }
            // Se c'pè livello 2, creo la struttura gerarchica
            else {
                // Creo il LIVELLO 2 (figlio) se non esiste
                if (!raggruppati[valore1].childrenMap[valore2]) {
                    raggruppati[valore1].childrenMap[valore2] = {
                        id: `${valore1}-${valore2}`,
                        label: valore2,
                        periodi: {},
                        children: [],
                        childrenMap: {},
                        targheMap: {}
                    };
                }

                const figlio = raggruppati[valore1].childrenMap[valore2];
                figlio.targheMap[riga.targa] = riga.data_immatricolazione;

                // Se non c'è livello 3, accumulo nel livello 2
                if (!campoLivello3) {
                    if (!figlio.periodi[chiavePeriodo]) {
                        figlio.periodi[chiavePeriodo] = creaPeriodoVuoto();
                    }
                    figlio.periodi[chiavePeriodo].kmPercorsi += kmPercorsi;
                    figlio.periodi[chiavePeriodo].oreGuidate += oreGuidate;
                    figlio.periodi[chiavePeriodo].velMediaPesata += velMedia * oreGuidate;
                }
                // Se c'è livello 3, creo il nipote
                else {
                    // Creo il LIVELLO 3 (nipote) se non esiste
                    if (!figlio.childrenMap[valore3]) {
                        figlio.childrenMap[valore3] = {
                            id: `${valore1}-${valore2}-${valore3}`,
                            label: valore3,
                            periodi: {},
                            children: [],
                            childrenMap: {},
                            targheMap: {}
                        };
                    }

                    const nipote = figlio.childrenMap[valore3];
                    nipote.targheMap[riga.targa] = riga.data_immatricolazione;

                    // Se non c'è livello 4, accumulo nel livello 3
                    if (!campoLivello4) {
                        if (!nipote.periodi[chiavePeriodo]) {
                            nipote.periodi[chiavePeriodo] = creaPeriodoVuoto();
                        }
                        nipote.periodi[chiavePeriodo].kmPercorsi += kmPercorsi;
                        nipote.periodi[chiavePeriodo].oreGuidate += oreGuidate;
                        nipote.periodi[chiavePeriodo].velMediaPesata += velMedia * oreGuidate;
                    }
                    // Se c'è livello 4, creo il pronipote
                    else {
                        // Creo il LIVELLO 4 (pronipote) se non esiste
                        if (!nipote.childrenMap[valore4]) {
                            nipote.childrenMap[valore4] = {
                                id: `${valore1}-${valore2}-${valore3}-${valore4}`,
                                label: valore4,
                                periodi: {},
                                children: [],  // nessun livello 5
                                targheMap: {}
                            };
                        }

                        const pronipote = nipote.childrenMap[valore4];
                        pronipote.targheMap[riga.targa] = riga.data_immatricolazione;

                        // Accumulo nel livello 4 (pronipote)
                        if (!pronipote.periodi[chiavePeriodo]) {
                            pronipote.periodi[chiavePeriodo] = creaPeriodoVuoto();
                        }
                        pronipote.periodi[chiavePeriodo].kmPercorsi += kmPercorsi;
                        pronipote.periodi[chiavePeriodo].oreGuidate += oreGuidate;
                        pronipote.periodi[chiavePeriodo].velMediaPesata += velMedia * oreGuidate;
                    }
                }
            }
        }

        // PASSO 2.5: Converto childrenMap in array e calcolo totali
        // (dal basso verso l'alto: nipoti → figli → padri)

        const sommaPeriodiFigli = (padre) => {
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];
                const periodiFiglio = Object.keys(figlio.periodi);

                for (let k = 0; k < periodiFiglio.length; k++) {
                    const chiave = periodiFiglio[k];
                    const dati = figlio.periodi[chiave];

                    if (!padre.periodi[chiave]) {
                        padre.periodi[chiave] = creaPeriodoVuoto();
                    }

                    padre.periodi[chiave].kmPercorsi += dati.kmPercorsi;
                    padre.periodi[chiave].oreGuidate += dati.oreGuidate;
                    padre.periodi[chiave].velMediaPesata += dati.velMediaPesata || 0;
                }
            }
        };

        // Processo ogni padre
        const padri = Object.keys(raggruppati);
        for (let i = 0; i < padri.length; i++) {
            const padre = raggruppati[padri[i]];

            // Converto childrenMap livello 2 in array
            padre.children = Object.values(padre.childrenMap || {});
            delete padre.childrenMap;

            // Per ogni figlio
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];

                // Converto childrenMap livello 3 in array
                figlio.children = Object.values(figlio.childrenMap || {});
                delete figlio.childrenMap;

                // Per ogni nipote 
                for (let k = 0; k < figlio.children.length; k++) {
                    const nipote = figlio.children[k];

                    // Converto childrenMap livello 4 in array
                    nipote.children = Object.values(nipote.childrenMap || {});
                    delete nipote.childrenMap;

                    // Se ci sono pronipoti, sommo i loro periodi al nipote
                    if (nipote.children.length > 0) {
                        sommaPeriodiFigli(nipote);
                    }
                }

                // Se ci sono nipoti, sommo i loro periodi al figlio
                if (figlio.children.length > 0) {
                    sommaPeriodiFigli(figlio);
                }
            }

            // Se ci sono figli, sommo i loro periodi al padre
            if (padre.children.length > 0) {
                sommaPeriodiFigli(padre);
            }
        }
        // PASSO 3: Creo i dati aggregati (TRIMESTRE, ANNO, TOTALE)
        // Mappa per sapere a quale trimestre appartiene ogni mese
        const meseToTrimestre = {
            '01': 'trim1', '02': 'trim1', '03': 'trim1',
            '04': 'trim2', '05': 'trim2', '06': 'trim2',
            '07': 'trim3', '08': 'trim3', '09': 'trim3',
            '10': 'trim4', '11': 'trim4', '12': 'trim4'
        };

        // Funzione helper per calcolare aggregati di un elemento (padre o figlio)
        const calcolaAggregati = (elemento) => {
            const trimestri = {};
            const anni = {};
            let totale = { kmPercorsi: 0, oreGuidate: 0, velMediaPesata: 0 };

            // Prima calcolo velMedia per i periodi mensili usando velMediaPesata
            const periodiMensili = Object.keys(elemento.periodi);
            for (let j = 0; j < periodiMensili.length; j++) {
                const chiave = periodiMensili[j];
                const periodo = elemento.periodi[chiave];

                // Se è un periodo mensile (formato "YYYY-MM")
                if (chiave.match(/^\d{4}-\d{2}$/)) {
                    // Calcolo velMedia come media ponderata: velMediaPesata / oreGuidate
                    if (periodo.oreGuidate > 0) {
                        periodo.velMedia = periodo.velMediaPesata / periodo.oreGuidate;
                    } else {
                        periodo.velMedia = 0;
                    }

                    // Calcolo kmOra
                    if (periodo.oreGuidate > 0) {
                        periodo.kmOra = periodo.kmPercorsi / periodo.oreGuidate;
                    }

                    // Estraggo anno e mese
                    const parti = chiave.split('-');
                    const anno = parti[0];
                    const mese = parti[1];
                    const trimestre = meseToTrimestre[mese];
                    const chiaveTrimestre = `${anno}-${trimestre}`;

                    // Accumulo nel trimestre (media ponderata)
                    if (!trimestri[chiaveTrimestre]) {
                        trimestri[chiaveTrimestre] = { kmPercorsi: 0, oreGuidate: 0, velMediaPesata: 0 };
                    }
                    trimestri[chiaveTrimestre].kmPercorsi += periodo.kmPercorsi;
                    trimestri[chiaveTrimestre].oreGuidate += periodo.oreGuidate;
                    trimestri[chiaveTrimestre].velMediaPesata += periodo.velMediaPesata || 0;

                    // Accumulo nell'anno (media ponderata)
                    if (!anni[anno]) {
                        anni[anno] = { kmPercorsi: 0, oreGuidate: 0, velMediaPesata: 0 };
                    }
                    anni[anno].kmPercorsi += periodo.kmPercorsi;
                    anni[anno].oreGuidate += periodo.oreGuidate;
                    anni[anno].velMediaPesata += periodo.velMediaPesata || 0;

                    // Accumulo nel totale (media ponderata)
                    totale.kmPercorsi += periodo.kmPercorsi;
                    totale.oreGuidate += periodo.oreGuidate;
                    totale.velMediaPesata += periodo.velMediaPesata || 0;
                }
            }

            // Aggiungo i trimestri
            const chiaveTrimestri = Object.keys(trimestri);
            for (let j = 0; j < chiaveTrimestri.length; j++) {
                const chiave = chiaveTrimestri[j];
                const trim = trimestri[chiave];
                elemento.periodi[chiave] = {
                    kmPercorsi: trim.kmPercorsi,
                    oreGuidate: trim.oreGuidate,
                    velMedia: trim.oreGuidate > 0 ? trim.velMediaPesata / trim.oreGuidate : 0,
                    kmOra: trim.oreGuidate > 0 ? trim.kmPercorsi / trim.oreGuidate : 0,
                    costoTotaleAutomezzi: 0,
                    costoKm: 0
                };
            }

            // Aggiungo gli anni
            const chiaveAnni = Object.keys(anni);
            for (let j = 0; j < chiaveAnni.length; j++) {
                const chiave = chiaveAnni[j];
                const annoData = anni[chiave];
                elemento.periodi[chiave] = {
                    kmPercorsi: annoData.kmPercorsi,
                    oreGuidate: annoData.oreGuidate,
                    velMedia: annoData.oreGuidate > 0 ? annoData.velMediaPesata / annoData.oreGuidate : 0,
                    kmOra: annoData.oreGuidate > 0 ? annoData.kmPercorsi / annoData.oreGuidate : 0,
                    costoTotaleAutomezzi: 0,
                    costoKm: 0
                };
            }

            // Aggiungo il totale
            elemento.periodi['totale'] = {
                kmPercorsi: totale.kmPercorsi,
                oreGuidate: totale.oreGuidate,
                velMedia: totale.oreGuidate > 0 ? totale.velMediaPesata / totale.oreGuidate : 0,
                kmOra: totale.oreGuidate > 0 ? totale.kmPercorsi / totale.oreGuidate : 0,
                costoTotaleAutomezzi: 0,
                costoKm: 0
            };

            // Vetustà (età odierna) — media tra le targhe uniche dell'elemento
            elemento.vetusta = calcolaVetustaMedia(elemento.targheMap);
        };

        // Applico la funzione a tutti i livelli
        // Ordine: pronipoti → nipoti → figli → padri
        const listaPadri = Object.keys(raggruppati);
        for (let i = 0; i < listaPadri.length; i++) {
            const padre = raggruppati[listaPadri[i]];

            // Per ogni figlio 
            for (let j = 0; j < padre.children.length; j++) {
                const figlio = padre.children[j];

                // Per ogni nipote 
                for (let k = 0; k < figlio.children.length; k++) {
                    const nipote = figlio.children[k];

                    // Calcolo aggregati per ogni pronipote 
                    for (let l = 0; l < nipote.children.length; l++) {
                        calcolaAggregati(nipote.children[l]);
                    }

                    // Calcolo aggregati per il nipote 
                    calcolaAggregati(nipote);
                }

                // Calcolo aggregati per il figlio 
                calcolaAggregati(figlio);
            }

            // Calcolo aggregati per il padre 
            calcolaAggregati(padre);
        }

        return Object.values(raggruppati);
    };


    // FUNZIONE PER CALCOLARE I TOTALI
    // Somma tutti i valori di ogni settore per ottenere i totali
    const calcolaTotali = (datiTrasformati, periodiDaMostrare) => {
        const totali = {};

        // creo un oggetto vuoto per ogni periodo
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            const periodo = periodiDaMostrare[i];
            totali[periodo.chiave] = {
                kmPercorsi: 0,
                oreGuidate: 0,
                velMedia: 0,
                velMediaPesata: 0,
                kmOra: 0,
                costoKm: 0,
                costoTotaleAutomezzi: 0,
            };
        }

        // sommo i valori di ogni settore
        for (let i = 0; i < datiTrasformati.length; i++) {
            const riga = datiTrasformati[i];

            for (let j = 0; j < periodiDaMostrare.length; j++) {
                const periodo = periodiDaMostrare[j];

                // Se questo periodo esiste nei dati della riga
                if (riga.periodi[periodo.chiave]) {
                    const datiPeriodo = riga.periodi[periodo.chiave];

                    // Sommo km e ore
                    totali[periodo.chiave].kmPercorsi += datiPeriodo.kmPercorsi;
                    totali[periodo.chiave].oreGuidate += datiPeriodo.oreGuidate;

                    // Accumulo per la media ponderata della velocità
                    totali[periodo.chiave].velMediaPesata += datiPeriodo.velMedia * datiPeriodo.oreGuidate;
                }
            }
        }

        // calcolo velMedia e kmOra per ogni periodo
        for (let i = 0; i < periodiDaMostrare.length; i++) {
            const periodo = periodiDaMostrare[i];
            const t = totali[periodo.chiave];

            // Calcolo la media ponderata della velocità
            if (t.oreGuidate > 0) {
                t.velMedia = t.velMediaPesata / t.oreGuidate;
            }

            // Calcolo kmOra = km totali / ore totali
            if (t.oreGuidate > 0) {
                t.kmOra = t.kmPercorsi / t.oreGuidate;
            }
        }

        return totali;
    };



    // FUNZIONE PER CONTROLLARE SE UNA COLONNA È VISIBILE
    // Questa funzione riceve il nome di una colonna (es. 'km-percorsi')
    // e restituisce TRUE se la colonna deve essere mostrata,
    // oppure FALSE se deve essere nascosta.
    const isColonnaVisibile = (nomeColonna) => {
        // Se nessun indicatore selezionato, mostro tutte le colonne
        if (selectedIndicatore.length === 0) {
            return true;
        }

        // Controllo se la colonna è tra quelle selezionate
        // .some() restituisce true se ALMENO un elemento soddisfa la condizione
        const colonnaSelezionata = selectedIndicatore.some(
            (opzione) => opzione.value === nomeColonna
        );

        return colonnaSelezionata;
    }

    // TRASFORMO I DATI E CALCOLO I TOTALI
    // Chiamo la funzione trasformaDati passando i dati del backend
    const datiTrasformati = trasformaDati(datiBackend);

    const periodiDaMostrare = getPeriodiDaMostrare();

    // Calcolo i totali per ogni periodo
    const totaliPerPeriodo = calcolaTotali(datiTrasformati, periodiDaMostrare);

    // Vetustà globale: media sulle targhe uniche di tutti i padri
    const targheGlobali = {};
    for (let i = 0; i < datiTrasformati.length; i++) {
        Object.assign(targheGlobali, datiTrasformati[i].targheMap || {});
    }
    const vetustaTotale = calcolaVetustaMedia(targheGlobali);

    // Calcolo quante metriche sono visibili (serve per colSpan)
    let colonnePerPeriodo = 0;
    if (isColonnaVisibile('km-percorsi')) colonnePerPeriodo++;
    if (isColonnaVisibile('ore-guidate')) colonnePerPeriodo++;
    if (isColonnaVisibile('km-ora')) colonnePerPeriodo++;
    if (isColonnaVisibile('vel-media')) colonnePerPeriodo++;
    if (isColonnaVisibile('costo-km')) colonnePerPeriodo++;
    if (isColonnaVisibile('costo-totale-automezzi')) colonnePerPeriodo++;
    if (isColonnaVisibile('vetusta')) colonnePerPeriodo++;

    // Booleani per sapere cosa è selezionato nel dropdown COLONNE
    const haAnnoSelezionato = selectedColonne.some(opt => opt.value === 'anno');
    const haTrimestreSelezionato = selectedColonne.some(opt => opt.value === 'trimestre');
    const haMeseSelezionato = selectedColonne.some(opt => opt.value === 'mese');

    return (
        <div className="controllo-km-ore-container">
            {isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">
                        Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                    </p>
                </div>
            ) : isDataReady ? (
                <>
                    {/* Sezione Tabella */}
                    <div className={`controllo-km-ore-table-section${isFullScreenTable ? ' fullscreen-table' : ''}`}>
                        <RotateDevice active={isFullScreenTable} />

                        <div className="controllo-km-ore-table-header">
                            <span className="controllo-km-ore-table-title">Totale Km Percorsi</span>

                            <div className="controllo-km-ore-table-header-btn">
                                <button
                                    className="controllo-km-ore-download-btn"
                                    onClick={() => handleDownloadFile(prepareExportData())}
                                    disabled={datiTrasformati.length === 0}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>

                                <button
                                    className="controllo-km-ore-fullscreen-btn"
                                    onClick={() => setIsFullScreenTable(!isFullScreenTable)}
                                    title={isFullScreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullScreenTable ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            {/* {isLoading && <span className="controllo-km-ore-loading">Caricamento...</span>} */}
                        </div>


                        {/* Riga configurazione con i 3 dropdown */}
                        <div className="controllo-km-ore-config-row">
                            <div className="controllo-km-ore-config-item">
                                <label>COLONNE</label>
                                <Select
                                    classNamePrefix="controllo-km-ore-select"
                                    placeholder="Selezioni multiple"
                                    value={selectedColonne}
                                    options={colonneOptions}
                                    onChange={setSelectedColonne}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>

                            <div className="controllo-km-ore-config-item">
                                <label>RIGHE</label>
                                <Select
                                    classNamePrefix="controllo-km-ore-select"
                                    placeholder="Selezioni multiple"
                                    value={selectedRighe}
                                    options={righeOptions}
                                    onChange={setSelectedRighe}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>

                            <div className="controllo-km-ore-config-item">
                                <label>INDICATORE</label>
                                <Select
                                    classNamePrefix="controllo-km-ore-select"
                                    placeholder="Tutte"
                                    value={selectedIndicatore}
                                    options={indicatoreOptions}
                                    onChange={setSelectedIndicatore}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    components={{ MultiValue: MultiValueContainer }}
                                />
                            </div>
                        </div>

                        <div className="controllo-km-ore-table-wrapper">
                            <table className="controllo-km-ore-table">
                                <thead>
                                    {/* HEADER GERARCHICO
                                - RIGA ANNO: si mostra se haAnnoSelezionato
                                - RIGA TRIMESTRE: si mostra se haTrimestreSelezionato
                                - RIGA MESE: si mostra se haMeseSelezionato
                                - RIGA INDICATORI: sempre visibile */}

                                    {/* RIGA ANNO */}
                                    {haAnnoSelezionato && (
                                        <tr>
                                            <th className="th-settore">Anno</th>
                                            {(() => {
                                                let celle = [];

                                                // Per ogni anno, creo una cella con colSpan calcolato dinamicamente
                                                for (let i = 0; i < anniDisponibili.length; i++) {
                                                    const anno = anniDisponibili[i];

                                                    // Calcolo colSpan per questo anno
                                                    // Dipende da cosa c'è sotto: Mese (n validi) > Trimestre (n validi) > niente (1)
                                                    let colSpanAnno = colonnePerPeriodo;
                                                    if (haMeseSelezionato) {
                                                        // Conto quanti mesi validi ci sono per questo anno
                                                        let mesiValidi = 0;
                                                        for (let m = 1; m <= 12; m++) {
                                                            if (isMeseValido(anno, m)) mesiValidi++;
                                                        }
                                                        colSpanAnno = mesiValidi * colonnePerPeriodo;
                                                    } else if (haTrimestreSelezionato) {
                                                        // Conto quanti trimestri validi ci sono per questo anno
                                                        let trimValidi = 0;
                                                        for (let t = 1; t <= 4; t++) {
                                                            if (isTrimestreValido(anno, t)) trimValidi++;
                                                        }
                                                        colSpanAnno = trimValidi * colonnePerPeriodo;
                                                    }

                                                    // Aggiungo la cella solo se ha almeno un trimestre valido
                                                    if (colSpanAnno > 0) {
                                                        celle.push(
                                                            <th key={anno} colSpan={colSpanAnno} className="th-periodo">{anno}</th>
                                                        );
                                                    }
                                                }

                                                // Calcolo rowSpan per "Totale"
                                                // Deve "saltare" le righe sotto (Trimestre e/o Mese)
                                                let rowSpanTotale = 1;
                                                if (haTrimestreSelezionato) rowSpanTotale++;
                                                if (haMeseSelezionato) rowSpanTotale++;

                                                // Aggiungo "Totale" alla fine
                                                celle.push(
                                                    <th
                                                        key="totale"
                                                        colSpan={colonnePerPeriodo}
                                                        rowSpan={rowSpanTotale}
                                                        className="th-periodo th-totale-header"
                                                    >
                                                        Totale
                                                    </th>
                                                );

                                                return celle;
                                            })()}
                                        </tr>
                                    )}

                                    {/* RIGA TRIMESTRE */}
                                    {haTrimestreSelezionato && (
                                        <tr>
                                            <th className="th-settore">Trimestre</th>
                                            {(() => {
                                                let celle = [];

                                                // Mesi che appartengono a ciascun trimestre
                                                const mesiPerTrimestre = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };

                                                // Calcolo colSpan per trimestre in base ai mesi validi
                                                const getColSpanTrimestre = (anno, trim) => {
                                                    if (!haMeseSelezionato) return colonnePerPeriodo;
                                                    const mesiValidi = mesiPerTrimestre[trim].filter(m => isMeseValido(anno, m)).length;
                                                    return mesiValidi * colonnePerPeriodo;
                                                };

                                                // Per ogni anno, creo solo i trimestri esistenti
                                                for (let i = 0; i < anniDisponibili.length; i++) {
                                                    const anno = anniDisponibili[i];
                                                    for (let t = 1; t <= 4; t++) {
                                                        if (isTrimestreValido(anno, t)) {
                                                            celle.push(<th key={`${anno}-t${t}`} colSpan={getColSpanTrimestre(anno, t)} className="th-periodo">Q{t} {anno}</th>);
                                                        }
                                                    }
                                                }

                                                // Aggiungo Totale solo se Anno non è selezionato (altrimenti è già nella riga Anno)
                                                if (!haAnnoSelezionato) {
                                                    let rowSpanTotale = 1;
                                                    if (haMeseSelezionato) rowSpanTotale++;
                                                    celle.push(
                                                        <th
                                                            key="totale"
                                                            colSpan={colonnePerPeriodo}
                                                            rowSpan={rowSpanTotale}
                                                            className="th-periodo th-totale-header"
                                                        >
                                                            Totale
                                                        </th>
                                                    );
                                                }

                                                return celle;
                                            })()}
                                        </tr>
                                    )}

                                    {/* RIGA MESE */}
                                    {haMeseSelezionato && (
                                        <tr>
                                            <th className="th-settore">Mese</th>
                                            {(() => {
                                                let celle = [];

                                                // Per ogni anno, creo solo i mesi esistenti
                                                for (let i = 0; i < anniDisponibili.length; i++) {
                                                    const anno = anniDisponibili[i];
                                                    for (let m = 0; m < 12; m++) {
                                                        // m va da 0 a 11, ma isMeseValido vuole mesi da 1 a 12
                                                        if (isMeseValido(anno, m + 1)) {
                                                            celle.push(
                                                                <th key={`${anno}-${m}`} colSpan={colonnePerPeriodo} className="th-periodo">
                                                                    {nomiMesi[m]} {anno}
                                                                </th>
                                                            );
                                                        }
                                                    }
                                                }

                                                // Aggiungo Totale solo se né Anno né Trimestre sono selezionati
                                                if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                                    celle.push(
                                                        <th
                                                            key="totale"
                                                            colSpan={colonnePerPeriodo}
                                                            className="th-periodo th-totale-header"
                                                        >
                                                            CUMULATO
                                                        </th>
                                                    );
                                                }

                                                return celle;
                                            })()}
                                        </tr>
                                    )}

                                    {/* RIGA INDICATORI (sempre visibile) */}
                                    <tr>
                                        <th className="th-settore">Settore</th>
                                        {periodiDaMostrare.map((periodo) => {
                                            // Se è il periodo "totale", aggiungo classe extra per stile diverso
                                            const classeTh = periodo.chiave === 'totale'
                                                ? 'th-metrica th-metrica-totale'
                                                : 'th-metrica';

                                            return (
                                                <React.Fragment key={periodo.chiave}>
                                                    {isColonnaVisibile('km-percorsi') && <th className={classeTh}>Km Percorsi</th>}
                                                    {isColonnaVisibile('ore-guidate') && <th className={classeTh}>Ore Guidate</th>}
                                                    {isColonnaVisibile('vetusta') && <th className={classeTh}>Vetustà</th>}
                                                    {isColonnaVisibile('km-ora') && <th className={classeTh}>Km all'ora</th>}
                                                    {isColonnaVisibile('vel-media') && <th className={classeTh}>Vel Media</th>}
                                                    {isColonnaVisibile('costo-km') && <th className={classeTh}>Costo al Km</th>}
                                                    {isColonnaVisibile('costo-totale-automezzi') && <th className={classeTh}>Costo Totale Automezzi</th>}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* CICLO SU OGNI RIGA DI mockData */}
                                    {datiTrasformati.map((row) => {

                                        // Controllo se questa riga ha dei figli
                                        const haFigli = row.children.length > 0;

                                        // Controllo se questa riga è espansa (aperta)
                                        const rigaAperta = isRowExpanded(row.id);

                                        // Decido quale icona mostrare
                                        let icona = '•';
                                        if (haFigli) {
                                            if (rigaAperta) {
                                                icona = '▼';
                                            } else {
                                                icona = '▶';
                                            }
                                        }

                                        // Decido se la riga è cliccabile
                                        let classeSettore = 'td-settore td-indent-0';
                                        if (haFigli) {
                                            classeSettore = 'td-settore td-indent-0 td-expandable';
                                        }

                                        return (
                                            <React.Fragment key={row.id}>

                                                {/*  RIGA PRINCIPALE (livello 1)*/}
                                                <tr className="tr-level-1">
                                                    <td
                                                        className={classeSettore}
                                                        onClick={() => {
                                                            // Quando clicco, se ha figli li espando/chiudo
                                                            if (haFigli) {
                                                                toggleRowExpansion(row.id);
                                                            }
                                                        }}
                                                    >
                                                        <span className="expand-icon">{icona}</span>
                                                        <span className="level-value">{row.label}</span>
                                                    </td>
                                                    {periodiDaMostrare.map((periodo) => {
                                                        // Se il periodo non esiste, uso valori a 0
                                                        const datiPeriodo = row.periodi[periodo.chiave] || {
                                                            kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
                                                            kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
                                                        };
                                                        return (
                                                            <React.Fragment key={periodo.chiave}>
                                                                {isColonnaVisibile('km-percorsi') && <td>{formatNumber(datiPeriodo.kmPercorsi)}</td>}
                                                                {isColonnaVisibile('ore-guidate') && <td>{formatNumber(datiPeriodo.oreGuidate)}</td>}
                                                                {isColonnaVisibile('vetusta') && <td>{formatNumber(row.vetusta || 0)}</td>}
                                                                {isColonnaVisibile('km-ora') && <td>{formatNumber(datiPeriodo.kmOra)}</td>}
                                                                {isColonnaVisibile('vel-media') && <td>{formatNumber(datiPeriodo.velMedia)}</td>}
                                                                {isColonnaVisibile('costo-km') && <td>{formatNumber(datiPeriodo.costoKm)}</td>}
                                                                {isColonnaVisibile('costo-totale-automezzi') && <td>{formatNumber(datiPeriodo.costoTotaleAutomezzi)}</td>}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tr>

                                                {/* Mostro i figli solo se la riga padre è aperta */}
                                                {rigaAperta && row.children.map((figlio) => {
                                                    // Controllo se questo figlio ha nipoti 
                                                    const figlioHaNipoti = figlio.children && figlio.children.length > 0;
                                                    const figlioAperto = isRowExpanded(figlio.id);

                                                    // Icona per il figlio: freccia se ha nipoti, pallino altrimenti
                                                    let iconaFiglio = '•';
                                                    if (figlioHaNipoti) {
                                                        iconaFiglio = figlioAperto ? '▼' : '▶';
                                                    }

                                                    // Classe CSS per il figlio
                                                    let classeFiglio = 'td-settore td-indent-1';
                                                    if (figlioHaNipoti) {
                                                        classeFiglio = 'td-settore td-indent-1 td-expandable';
                                                    }

                                                    return (
                                                        <React.Fragment key={figlio.id}>
                                                            {/* RIGA FIGLIO */}
                                                            <tr className="tr-level-2">
                                                                <td
                                                                    className={classeFiglio}
                                                                    onClick={() => {
                                                                        if (figlioHaNipoti) {
                                                                            toggleRowExpansion(figlio.id);
                                                                        }
                                                                    }}
                                                                >
                                                                    <span className="expand-icon">{iconaFiglio}</span>
                                                                    <span className="level-value">{figlio.label}</span>
                                                                </td>
                                                                {periodiDaMostrare.map((periodo) => {
                                                                    const datiFiglio = figlio.periodi[periodo.chiave] || {
                                                                        kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
                                                                        kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
                                                                    };
                                                                    return (
                                                                        <React.Fragment key={periodo.chiave}>
                                                                            {isColonnaVisibile('km-percorsi') && <td>{formatNumber(datiFiglio.kmPercorsi)}</td>}
                                                                            {isColonnaVisibile('ore-guidate') && <td>{formatNumber(datiFiglio.oreGuidate)}</td>}
                                                                            {isColonnaVisibile('vetusta') && <td>{formatNumber(figlio.vetusta || 0)}</td>}
                                                                            {isColonnaVisibile('km-ora') && <td>{formatNumber(datiFiglio.kmOra)}</td>}
                                                                            {isColonnaVisibile('vel-media') && <td>{formatNumber(datiFiglio.velMedia)}</td>}
                                                                            {isColonnaVisibile('costo-km') && <td>{formatNumber(datiFiglio.costoKm)}</td>}
                                                                            {isColonnaVisibile('costo-totale-automezzi') && <td>{formatNumber(datiFiglio.costoTotaleAutomezzi)}</td>}
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </tr>

                                                            {/* Mostro i nipoti solo se il figlio è aperto */}
                                                            {figlioAperto && figlio.children.map((nipote) => {
                                                                // Controllo se questo nipote ha pronipoti 
                                                                const nipoteHaPronipoti = nipote.children && nipote.children.length > 0;
                                                                const nipoteAperto = isRowExpanded(nipote.id);

                                                                // Icona per il nipote: freccia se ha pronipoti, pallino altrimenti
                                                                let iconaNipote = '•';
                                                                if (nipoteHaPronipoti) {
                                                                    iconaNipote = nipoteAperto ? '▼' : '▶';
                                                                }

                                                                // Classe CSS per il nipote
                                                                let classeNipote = 'td-settore td-indent-2';
                                                                if (nipoteHaPronipoti) {
                                                                    classeNipote = 'td-settore td-indent-2 td-expandable';
                                                                }

                                                                return (
                                                                    <React.Fragment key={nipote.id}>
                                                                        {/* RIGA NIPOTE */}
                                                                        <tr className="tr-level-3">
                                                                            <td
                                                                                className={classeNipote}
                                                                                onClick={() => {
                                                                                    if (nipoteHaPronipoti) {
                                                                                        toggleRowExpansion(nipote.id);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <span className="expand-icon">{iconaNipote}</span>
                                                                                <span className="level-value">{nipote.label}</span>
                                                                            </td>
                                                                            {periodiDaMostrare.map((periodo) => {
                                                                                const datiNipote = nipote.periodi[periodo.chiave] || {
                                                                                    kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
                                                                                    kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
                                                                                };
                                                                                return (
                                                                                    <React.Fragment key={periodo.chiave}>
                                                                                        {isColonnaVisibile('km-percorsi') && <td>{formatNumber(datiNipote.kmPercorsi)}</td>}
                                                                                        {isColonnaVisibile('ore-guidate') && <td>{formatNumber(datiNipote.oreGuidate)}</td>}
                                                                                        {isColonnaVisibile('vetusta') && <td>{formatNumber(nipote.vetusta || 0)}</td>}
                                                                                        {isColonnaVisibile('km-ora') && <td>{formatNumber(datiNipote.kmOra)}</td>}
                                                                                        {isColonnaVisibile('vel-media') && <td>{formatNumber(datiNipote.velMedia)}</td>}
                                                                                        {isColonnaVisibile('costo-km') && <td>{formatNumber(datiNipote.costoKm)}</td>}
                                                                                        {isColonnaVisibile('costo-totale-automezzi') && <td>{formatNumber(datiNipote.costoTotaleAutomezzi)}</td>}
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                        </tr>

                                                                        {/* Mostro i pronipoti solo se il nipote è aperto */}
                                                                        {nipoteAperto && nipote.children.map((pronipote) => (
                                                                            <tr key={pronipote.id} className="tr-level-4">
                                                                                <td className="td-settore td-indent-3">
                                                                                    <span className="expand-icon">•</span>
                                                                                    <span className="level-value">{pronipote.label}</span>
                                                                                </td>
                                                                                {periodiDaMostrare.map((periodo) => {
                                                                                    const datiPronipote = pronipote.periodi[periodo.chiave] || {
                                                                                        kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
                                                                                        kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
                                                                                    };
                                                                                    return (
                                                                                        <React.Fragment key={periodo.chiave}>
                                                                                            {isColonnaVisibile('km-percorsi') && <td>{formatNumber(datiPronipote.kmPercorsi)}</td>}
                                                                                            {isColonnaVisibile('ore-guidate') && <td>{formatNumber(datiPronipote.oreGuidate)}</td>}
                                                                                            {isColonnaVisibile('vetusta') && <td>{formatNumber(pronipote.vetusta || 0)}</td>}
                                                                                            {isColonnaVisibile('km-ora') && <td>{formatNumber(datiPronipote.kmOra)}</td>}
                                                                                            {isColonnaVisibile('vel-media') && <td>{formatNumber(datiPronipote.velMedia)}</td>}
                                                                                            {isColonnaVisibile('costo-km') && <td>{formatNumber(datiPronipote.costoKm)}</td>}
                                                                                            {isColonnaVisibile('costo-totale-automezzi') && <td>{formatNumber(datiPronipote.costoTotaleAutomezzi)}</td>}
                                                                                        </React.Fragment>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        ))}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}

                                            </React.Fragment>
                                        );
                                    })}

                                    {/* RIGA TOTALE (sempre visibile in fondo)*/}
                                    <tr className="tr-totali">
                                        <td className="td-settore"><strong>CUMULATO</strong></td>
                                        {periodiDaMostrare.map((periodo) => {
                                            // Se il periodo non esiste nei totali, uso valori a 0
                                            const datiTotale = totaliPerPeriodo[periodo.chiave] || {
                                                kmPercorsi: 0, oreGuidate: 0, velMedia: 0,
                                                kmOra: 0, costoKm: 0, costoTotaleAutomezzi: 0
                                            };
                                            return (
                                                <React.Fragment key={periodo.chiave}>
                                                    {isColonnaVisibile('km-percorsi') && <td><strong>{formatNumber(datiTotale.kmPercorsi)}</strong></td>}
                                                    {isColonnaVisibile('ore-guidate') && <td><strong>{formatNumber(datiTotale.oreGuidate)}</strong></td>}
                                                    {isColonnaVisibile('vetusta') && <td><strong>{formatNumber(vetustaTotale)}</strong></td>}
                                                    {isColonnaVisibile('km-ora') && <td><strong>{formatNumber(datiTotale.kmOra)}</strong></td>}
                                                    {isColonnaVisibile('vel-media') && <td><strong>{formatNumber(datiTotale.velMedia)}</strong></td>}
                                                    {isColonnaVisibile('costo-km') && <td><strong>{formatNumber(datiTotale.costoKm)}</strong></td>}
                                                    {isColonnaVisibile('costo-totale-automezzi') && <td><strong>{formatNumber(datiTotale.costoTotaleAutomezzi)}</strong></td>}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default ControlloKmOre;